/**
 * useQChat Hook (v2.0 - Action Chips)
 * Custom hook for managing Q AI chat state and SSE streaming
 *
 * Updated to use action chips instead of action cards
 */

import { useState, useCallback, useRef } from 'react';
import { getBalance, askQAI, getActionCost } from '../api/qaiApi';

// ═══════════════════════════════════════════════════════════════════════════
// WELCOME MESSAGE TEMPLATE - Now uses action chips
// ═══════════════════════════════════════════════════════════════════════════

const createWelcomeMessage = (userName) => ({
  id: 'welcome',
  role: 'assistant',
  content: `Good ${getGreeting()}, **${userName || 'Trader'}**! 👋

I'm Q, your AI trading coach. Here's what I can help with:`,
  showActionChips: true, // Flag to show action chips instead of cards
  timestamp: new Date().toISOString()
});

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

/**
 * Parse AI response for analysis data to display on the board
 * Detects performance analysis, signals, or custom data patterns
 */
const parseAnalysisFromResponse = (content, userQuery) => {
  if (!content || content.length < 50) return null;

  const lowerContent = content.toLowerCase();
  const lowerQuery = userQuery?.toLowerCase() || '';

  // Detect performance analysis keywords
  const isPerformance = lowerQuery.includes('performance') ||
    lowerQuery.includes('analyze') ||
    lowerQuery.includes('review') ||
    lowerContent.includes('win rate') ||
    lowerContent.includes('profit factor') ||
    lowerContent.includes('drawdown');

  // Detect signals keywords
  const isSignals = lowerQuery.includes('signal') ||
    lowerQuery.includes('entry') ||
    lowerQuery.includes('trade idea') ||
    lowerContent.includes('buy signal') ||
    lowerContent.includes('sell signal');

  // Detect trades/history keywords (E4)
  const isTrades = lowerQuery.includes('trade history') ||
    lowerQuery.includes('my trades') ||
    lowerQuery.includes('recent trades') ||
    lowerQuery.includes('show trades') ||
    lowerQuery.includes('trading history') ||
    lowerContent.includes('your trades') ||
    lowerContent.includes('trade history');

  if (isTrades) {
    return {
      type: 'trades',
      title: 'Your Trade History',
      subtitle: 'Synced from MT5 via QBridge',
      trades: extractTradesFromContent(content),
      summary: extractTradeSummary(content)
    };
  }

  if (isPerformance) {
    // Extract performance metrics (simplified parsing)
    return {
      type: 'performance',
      title: 'Your Trading Performance',
      subtitle: 'AI-Generated Analysis',
      stats: extractStatsFromContent(content),
      strengths: extractBulletPoints(content, ['strength', 'well', 'good', 'strong']),
      improvements: extractBulletPoints(content, ['improve', 'consider', 'reduce', 'watch']),
      potential: extractPotential(content)
    };
  }

  if (isSignals) {
    return {
      type: 'signals',
      title: 'Market Signals',
      signals: extractSignalsFromContent(content)
    };
  }

  // Generic analysis - display as custom
  if (content.length > 200) {
    return {
      type: 'custom',
      title: 'Q Analysis',
      content: content.substring(0, 500) + (content.length > 500 ? '...' : '')
    };
  }

  return null;
};

// Helper: Extract stats from content
const extractStatsFromContent = (content) => {
  const stats = [];

  // Try to find percentage values
  const winRateMatch = content.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:win|winning)/i);
  if (winRateMatch) {
    stats.push({ label: 'Win Rate', value: `${winRateMatch[1]}%`, color: parseFloat(winRateMatch[1]) >= 50 ? 'green' : 'red' });
  }

  const profitMatch = content.match(/(?:profit|gain|return)[^\d]*(\d+(?:\.\d+)?)\s*%?/i);
  if (profitMatch) {
    stats.push({ label: 'Total Return', value: `+${profitMatch[1]}%`, color: 'green' });
  }

  const tradesMatch = content.match(/(\d+)\s*(?:trades?|positions?)/i);
  if (tradesMatch) {
    stats.push({ label: 'Total Trades', value: tradesMatch[1], color: 'default' });
  }

  // Fallback if no stats found
  if (stats.length === 0) {
    stats.push({ label: 'Analysis', value: 'Complete', color: 'green' });
  }

  return stats;
};

// Helper: Extract bullet points
const extractBulletPoints = (content, keywords) => {
  const sentences = content.split(/[.!?]+/);
  const matches = sentences
    .filter(s => keywords.some(k => s.toLowerCase().includes(k)))
    .map(s => s.trim())
    .filter(s => s.length > 10 && s.length < 200)
    .slice(0, 3);

  return matches.length > 0 ? matches : null;
};

// Helper: Extract potential impact
const extractPotential = (content) => {
  const potentialMatch = content.match(/(?:potential|could|may|might)[^.]*(\d+(?:\.\d+)?%[^.]*)/i);
  return potentialMatch ? potentialMatch[1].trim() : null;
};

// Helper: Extract signals from content
const extractSignalsFromContent = (content) => {
  const signals = [];
  const pairs = ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD', 'USD/CAD', 'EUR/GBP', 'EURUSD', 'GBPUSD', 'USDJPY'];

  pairs.forEach(pair => {
    const pairRegex = new RegExp(pair.replace('/', '/?'), 'i');
    if (pairRegex.test(content)) {
      const isBuy = content.toLowerCase().includes('buy') || content.toLowerCase().includes('long');
      const isSell = content.toLowerCase().includes('sell') || content.toLowerCase().includes('short');

      signals.push({
        pair: pair.includes('/') ? pair : pair.slice(0, 3) + '/' + pair.slice(3),
        direction: isBuy ? 'BUY' : isSell ? 'SELL' : 'WAIT',
        entry: 'Market',
        score: Math.floor(Math.random() * 20) + 70 // Placeholder score
      });
    }
  });

  return signals.length > 0 ? signals : null;
};

// Helper: Extract trades from content (E4)
const extractTradesFromContent = (content) => {
  const trades = [];
  const pairs = ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD', 'USDCAD', 'EURGBP', 'XAUUSD', 'BTCUSD'];

  // Try to find trade mentions in content
  pairs.forEach(pair => {
    const pairRegex = new RegExp(`${pair}[^\\d]*(buy|sell|long|short)[^\\d]*([\\d.]+)`, 'gi');
    const matches = content.matchAll(pairRegex);

    for (const match of matches) {
      const type = match[1].toLowerCase();
      trades.push({
        id: `${pair}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        symbol: pair.slice(0, 3) + '/' + pair.slice(3),
        type: type === 'buy' || type === 'long' ? 'BUY' : 'SELL',
        volume: parseFloat(match[2]) || 0.1,
        profit: (Math.random() * 200 - 100), // Placeholder
        closeTime: new Date().toISOString()
      });
    }
  });

  return trades;
};

// Helper: Extract trade summary from content (E4)
const extractTradeSummary = (content) => {
  const summary = {};

  // Try to extract total trades
  const tradesMatch = content.match(/(\d+)\s*(?:total\s*)?trades?/i);
  if (tradesMatch) {
    summary.totalTrades = parseInt(tradesMatch[1], 10);
  }

  // Try to extract win rate
  const winRateMatch = content.match(/(\d+(?:\.\d+)?)\s*%?\s*(?:win|winning)\s*rate/i);
  if (winRateMatch) {
    summary.winRate = parseFloat(winRateMatch[1]);
  }

  // Try to extract total P&L
  const pnlMatch = content.match(/(?:total|net)\s*(?:p&?l|profit|return)[:\s]*\$?([-\d,.]+)/i);
  if (pnlMatch) {
    summary.totalPnL = parseFloat(pnlMatch[1].replace(/,/g, ''));
  }

  return Object.keys(summary).length > 0 ? summary : null;
};

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * useQChat Hook
 * @param {string} token - JWT token for API authentication
 * @param {string} userName - User's display name for personalized greeting
 * @param {function} onAnalysisData - Callback when AI provides analysis data for the board
 * @returns {object} Chat state and actions
 */
const useQChat = (token, userName = 'Trader', onAnalysisData = null) => {
  // State
  const [messages, setMessages] = useState([createWelcomeMessage(userName)]);
  const [balance, setBalance] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [error, setError] = useState(null);

  // Ref for abort controller
  const abortControllerRef = useRef(null);

  // Ref to store the callback
  const onAnalysisDataRef = useRef(onAnalysisData);
  onAnalysisDataRef.current = onAnalysisData;

  // ─────────────────────────────────────────────────────────────────────────
  // Refresh balance from API
  // ─────────────────────────────────────────────────────────────────────────
  const refreshBalance = useCallback(async () => {
    if (!token) return;

    try {
      const data = await getBalance(token);
      setBalance(data.balance);
      return data;
    } catch (err) {
      console.error('Failed to refresh balance:', err);
      return null;
    }
  }, [token]);

  // ─────────────────────────────────────────────────────────────────────────
  // Send message with SSE streaming
  // ─────────────────────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (content, actionType = 'chat') => {
    if (!token || !content.trim() || isLoading) return;

    setError(null);
    setIsLoading(true);

    // Add user message immediately
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);

    // Create placeholder for assistant response
    const assistantMessageId = `assistant-${Date.now()}`;
    const assistantMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isStreaming: true
    };

    setMessages(prev => [...prev, assistantMessage]);

    // Start SSE streaming
    abortControllerRef.current = askQAI(
      token,
      content,
      actionType,
      // onToken - append each token
      (tokenText) => {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, content: msg.content + tokenText }
              : msg
          )
        );
      },
      // onDone - update balance and mark complete
      (newBalance) => {
        setIsLoading(false);
        if (newBalance !== null) {
          setBalance(newBalance);
        }
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.id === assistantMessageId
              ? { ...msg, isStreaming: false }
              : msg
          );

          // Parse analysis data from the completed response
          const assistantMessage = updatedMessages.find(m => m.id === assistantMessageId);
          if (assistantMessage && onAnalysisDataRef.current) {
            const analysisData = parseAnalysisFromResponse(assistantMessage.content, content);
            if (analysisData) {
              onAnalysisDataRef.current(analysisData);
            }
          }

          return updatedMessages;
        });
        abortControllerRef.current = null;
      },
      // onError - handle errors
      (err) => {
        setIsLoading(false);
        abortControllerRef.current = null;

        if (err.code === 402) {
          // Insufficient tokens - show pricing modal
          setShowPricingModal(true);
          // Remove the empty assistant message
          setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
        } else {
          setError(err.message);
          // Update assistant message with error
          setMessages(prev =>
            prev.map(msg =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content: 'Sorry, I encountered an error. Please try again.',
                    isStreaming: false,
                    isError: true
                  }
                : msg
            )
          );
        }
      }
    );
  }, [token, isLoading]);

  // ─────────────────────────────────────────────────────────────────────────
  // Handle action card click (legacy)
  // ─────────────────────────────────────────────────────────────────────────
  const handleActionCard = useCallback((actionId) => {
    // Legacy action cards - kept for backward compatibility
    return { focusInput: true };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Handle action chip click (new mockup design)
  // ─────────────────────────────────────────────────────────────────────────
  const handleActionChip = useCallback((action) => {
    // action comes from QActionChips with { id, prompt, label, ... }
    if (!action?.prompt) return null;

    sendMessage(action.prompt, 'chat');
    return { sent: true };
  }, [sendMessage]);

  // ─────────────────────────────────────────────────────────────────────────
  // Cancel current stream
  // ─────────────────────────────────────────────────────────────────────────
  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);

      // Mark any streaming messages as complete
      setMessages(prev =>
        prev.map(msg =>
          msg.isStreaming
            ? { ...msg, isStreaming: false, content: msg.content + ' [cancelled]' }
            : msg
        )
      );
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Clear messages (reset to welcome)
  // ─────────────────────────────────────────────────────────────────────────
  const clearMessages = useCallback(() => {
    setMessages([createWelcomeMessage(userName)]);
    setError(null);
  }, [userName]);

  // ─────────────────────────────────────────────────────────────────────────
  // Close pricing modal
  // ─────────────────────────────────────────────────────────────────────────
  const closePricingModal = useCallback(() => {
    setShowPricingModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Get estimated cost for action
  // ─────────────────────────────────────────────────────────────────────────
  const getEstimatedCost = useCallback((actionType = 'chat') => {
    return getActionCost(actionType);
  }, []);

  return {
    // State
    messages,
    balance,
    isLoading,
    showPricingModal,
    error,

    // Actions
    sendMessage,
    handleActionCard,
    handleActionChip,
    refreshBalance,
    cancelStream,
    clearMessages,
    closePricingModal,
    getEstimatedCost,

    // Helpers
    isLowBalance: balance <= 15,
    canSend: !isLoading && balance > 0
  };
};

export default useQChat;
